import { ECategoryType } from '../../data/Constants';

import type { ICategory, IView, ISection } from '../../../types/ITypes';

/**
 * Checks if a category is a multi-numeric category based on its ID format
 * @param {string} categoryId - The category ID to check
 * @returns {boolean} True if the category ID has more than 2 parts separated by underscores
 */
export function checkIsMultiNumericCategory(categoryId: string): boolean {
  return categoryId?.split('_').length > 2;
}

/**
 * Finds and returns a category from a list based on the category code
 * @param {Array<ICategory>} list - Array of categories to search through
 * @param {string} code - The category code to find
 * @returns {ICategory} The category with the matching code
 */
export const findCategoryWithCode = (list: Array<ICategory>, code: string): ICategory => {
  const item: ICategory = list.find((categoryItem: ICategory) => categoryItem.code === code)!;
  return item;
};

/**
 * Adds a category at root level or as a sub-category
 * Validates if the category already exists and returns an error message if it does
 * @param {IView} view - The master view containing all sections
 * @param {ISection} selectedSection - The parent section to add the new category to (null for root level)
 * @param {{ id: string; name: string; type?: string }} newCategoryDetail - Details of the new category to add
 * @returns {string | Array<ISection>} Error message if category exists, otherwise the updated sections array
 */
export const addCategoryAndSubCategory = (
  view: IView,
  selectedSection: ISection,
  newCategoryDetail: { code: string; description: string; type: string }
) => {
  let isCategoryExist: boolean = false;
  let categoryWithNewCategory: Array<ISection> = [];
  const getDescription = () => {
    const description = newCategoryDetail?.description.toString().split(':');
    if (description.length > 1) {
      return description[1].trim();
    } else {
      return newCategoryDetail?.description;
    }
  };

  const newCategory: ISection = {
    code: newCategoryDetail?.code,
    description: getDescription(),
    contents: [],
    sections: [],
    hideAddIcon: false,
    type: newCategoryDetail.type,
    hideGeneral: false,
    action: '',
  };

  const checkIfExist = (sections: Array<ISection>) => {
    return sections.filter((section: ISection) => {
      if (section.description === newCategory.description || section.code === newCategory.code) {
        isCategoryExist = true;
      }
      if (!isCategoryExist && section.sections) {
        checkIfExist(section.sections);
      }
      return section;
    });
  };

  if (view?.sections !== undefined && view?.sections.length > 0) {
    checkIfExist(view.sections);
  }

  if (isCategoryExist) {
    return 'messages.category.categoryAlreadyExist';
  }

  if (selectedSection == null || selectedSection === undefined) {
    categoryWithNewCategory = [...view.sections, newCategory];
  } else {
    const addSectionInSubSection = (sections: ISection[]) => {
      const updateSections = sections.map((subSection: ISection) => {
        if (subSection.code === selectedSection.code) {
          const updatedSection = [
            ...(subSection.sections ?? []),
            {
              ...newCategory,
              hideAddIcon: newCategory.type === ECategoryType.MultiNumeric,
              hideGeneral: true,
            },
          ];
          subSection = { ...subSection, sections: updatedSection };
        }

        if (subSection.sections !== undefined) {
          const newSection: ISection[] = addSectionInSubSection(subSection.sections);
          const data = { ...subSection, sections: newSection };
          return data;
        }

        return subSection;
      });
      return updateSections;
    };

    categoryWithNewCategory = addSectionInSubSection(view.sections);
  }
  return categoryWithNewCategory;
};

/**
 * Updates the multi-numeric category information in the master view data
 * @param {IView | ISection} view - The master view or section to update
 * @param {string} previousCategoryId - Previous category id
 * @param {IOption} newCategory - New category object
 * @returns {IView | ISection} Updated view or section with the category information
 */
export function updateMultiNumericCategory(
  view: IView | ISection,
  previousCategoryId: string,
  newCategory: any
): IView | ISection {
  const updateSections = (sections: ISection[]): ISection[] => {
    return sections.map((section) => {
      if (section.code === previousCategoryId) {
        return {
          ...section,
          code: newCategory.code,
          description: newCategory.description,
        };
      }
      if (section.sections && section.sections.length > 0) {
        return {
          ...section,
          sections: updateSections(section.sections),
        };
      }
      return section;
    });
  };

  if (view.sections) {
    return {
      ...view,
      sections: updateSections(view.sections),
    };
  }

  return view;
}

/**
 * Recursively finds a section by its ID in the section tree
 * @param {ISection[]} sections - Array of sections to search through
 * @param {string} id - The ISection ID to find
 * @returns {ISection | null} The found ISection or null if not found
 */
export const findSectionById = (sections: ISection[], id: string): ISection | null => {
  for (const section of sections) {
    if (section.code === id) return section;
    if (section.sections) {
      const found = findSectionById(section.sections, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Creates a deep clone of sections array using structured clone
 * @param {ISection[]} sections - The sections array to clone
 * @returns {ISection[]} A deep copy of the sections array
 */
export const deepCloneSections = (sections: ISection[]): ISection[] => structuredClone(sections);

/**
 * Recursively finds a section by ID in the section tree
 * @param {ISection[]} nodes - Array of section nodes to search through
 * @param {string} id - The section ID to find
 * @returns {ISection | null} The found section or null if not found
 */
export const findSectionInTree = (nodes: ISection[], id: string): ISection | null => {
  for (const node of nodes) {
    if (node.code === id) return node;
    if (node.sections) {
      const found = findSectionInTree(node.sections, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Finds the parent section of a child section by ID
 * @param {ISection[]} sections - Array of sections to search through
 * @param {string} childId - The ID of the child section whose parent to find
 * @param {ISection | null} parent - The current parent section (used internally for recursion)
 * @returns {ISection | null} The parent section or null if not found
 */
export function findParent(
  sections: ISection[],
  childId: string,
  parent: ISection | null = null
): ISection | null {
  for (const section of sections) {
    if (section.code === childId) return parent;
    if (section.sections) {
      const found = findParent(section.sections, childId, section);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Finds and removes a node from the section tree, and locates the target node
 * @param {ISection[]} nodes - Array of section nodes
 * @param {string} draggedId - The ID of the node being dragged
 * @param {string} targetId - The ID of the target node
 * @returns {{ newSections: ISection[]; draggedNode: ISection | null; targetNode: ISection | null }}
 * Object containing updated sections, dragged node, and target node
 */
export function findAndRemoveNode(
  nodes: ISection[],
  draggedId: string,
  targetId: string
): { newSections: ISection[]; draggedNode: ISection | null; targetNode: ISection | null } {
  let draggedNode: ISection | null = null;
  let targetNode: ISection | null = null;

  function helper(nodes: ISection[]): ISection[] {
    return nodes.filter((node) => {
      if (node.code === draggedId) {
        draggedNode = node;
        return false;
      }
      if (node.code === targetId) {
        targetNode = node;
      }
      if (node.sections) {
        node.sections = helper(node.sections);
      }
      return true;
    });
  }

  const newSections = helper(nodes);
  return { newSections, draggedNode, targetNode };
}
